import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const useIntersectionObserver = (ref) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      { threshold: 0.3 } 
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref]);

  return isVisible;
};

const Counter = ({ targetNumber, isVisible }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isVisible && !hasAnimated) {
      let start = 0;
      const duration = 3000;
      const increment = targetNumber / (duration / 16.66); 

      const animateCounter = () => {
        start += increment;
        if (start < targetNumber) {
          setCount(Math.round(start));
          requestAnimationFrame(animateCounter);
        } else {
          setCount(targetNumber); 
          setHasAnimated(true);
        }
      };

      requestAnimationFrame(animateCounter);
    }
  }, [isVisible, targetNumber, hasAnimated]);

  return <span>{count}</span>;
};

const AboutUs = () => {
  const sectionRef = useRef(null);
  const isVisible = useIntersectionObserver(sectionRef);
  const { t } = useTranslation();
  const [popupVisible, setPopupVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleOpenPopup = () => {
    setPopupVisible(true);
    document.body.classList.add('no-scroll');
  };

  const handleClosePopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setPopupVisible(false);
      setIsClosing(false);
      document.body.classList.remove('no-scroll');
    }, 500);
  };

  const selectedService = {
    title: t("aboutUs.aboutUs"),
    details: t("aboutUs.description"),
  };

  return (
    <section
      id="about-us"
      ref={sectionRef}
      className="lg:flex lg:flex-row flex-col items-center justify-between px-5 max-w-[1290px] w-full mx-auto mt-32"
    >
      <div className="lg:w-[47%]">
        <h2 className="text-[#4F4F4F] font-[700] text-[16px] leading-[20.8px]">
          {t("aboutUs.aboutUs")}
        </h2>
        <h1 className="text-4xl font-bold text-gray-900 mt-4">{t("aboutUs.title")}</h1>
        <p className="text-gray-700 mt-6 leading-relaxed">
          {t("aboutUs.description")}
        </p>
        <button
          onClick={handleOpenPopup}
          className="text-[#F67E20] font-medium mt-8 inline-flex items-center gap-3"
        >
          {t("hero.learnMore")} <i className="fa-solid fa-chevron-down"></i>
        </button>

        <div className="mt-10 w-full">
          <div className="grid grid-cols-2 gap-8 text-center lg:-ml-12 -ml-2">
            <div>
              <h2 className="text-[#F67E20] text-[32px] lg:text-[32px] font-[900] leading-[40px] lg:leading-[56px]">
                <Counter targetNumber={3000} isVisible={isVisible} />+
              </h2>
              <p className="text-[14px] lg:text-[16px] text-[#4A4A4A] font-[500] leading-[20px] lg:leading-[24px]">
                {t('aboutUs.clientsAndPartners')}
              </p>
            </div>
            <div>
              <h2 className="text-[#F67E20] text-[32px] lg:text-[32px] font-[900] leading-[40px] lg:leading-[56px]">
                <Counter targetNumber={360000} isVisible={isVisible} />+
              </h2>
              <p className="text-[14px] lg:text-[16px] text-[#4A4A4A] font-[500] leading-[20px] lg:leading-[24px]">
                {t('aboutUs.customsGoods')}
              </p>
            </div>
            <div>
              <h2 className="text-[#F67E20] text-[32px] lg:text-[32px] font-[900] leading-[40px] lg:leading-[56px]">
                <Counter targetNumber={30000} isVisible={isVisible} />+
              </h2>
              <p className="text-[14px] lg:text-[16px] text-[#4A4A4A] font-[500] leading-[20px] lg:leading-[24px]">
                {t('aboutUs.transports')}
              </p>
            </div>
            <div>
              <h2 className="text-[#F67E20] text-[32px] lg:text-[32px] font-[900] leading-[40px] lg:leading-[56px]">
                <Counter targetNumber={300000} isVisible={isVisible} />T
              </h2>
              <p className="text-[14px] lg:text-[16px] text-[#4A4A4A] font-[500] leading-[20px] lg:leading-[24px]">
                {t('aboutUs.storedGoods')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <img
        src="/images/aboutus.jpeg"
        alt="About Us image"
        className="h-[354px] w-full lg:w-[45%] mt-20 lg:mt-0 object-cover rounded-lg"
        loading="lazy"
      />

      {popupVisible && (
  <div className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}>
    <div className={`bg-white p-8 rounded-lg lg:w-[70%] w-[90%] flex ${isClosing ? 'animate-slide-out' : 'animate-slide-in'}`}>
      <div className="w-full">
        <button
          onClick={handleClosePopup}
          className="text-gray-500 hover:text-gray-700 float-right"
        >
          ✕
        </button>
        <h3 className="text-[20px] font-bold mb-4">{selectedService.title}</h3>
        <div className="text-[#3D3D3D] max-h-[400px] overflow-y-auto">
          <div className="flex flex-col gap-4">
           <p className="font-bold">{t("aboutUs.p1")}</p> 
            <p>{t("aboutUs.p2")}</p>
            <p className="font-bold">{t("aboutUs.p3")}</p>
           <p>{t("aboutUs.p4")}</p>
            <p>{t("aboutUs.p5")}</p>
            <p className="font-bold">{t("aboutUs.p6")}</p>
            <p>
              <span className="font-bold">{t("aboutUs.p7").split(" ")[0]} </span>
              {t("aboutUs.p7").substring(t("aboutUs.p7").indexOf(" ") + 1)}
            </p>
            <p>
              <span className="font-bold">{t("aboutUs.p8").split(" ")[0]} </span>
              {t("aboutUs.p8").substring(t("aboutUs.p8").indexOf(" ") + 1)}
            </p>
            <p>
              <span className="font-bold">{t("aboutUs.p9").split(" ")[0]} </span>
              {t("aboutUs.p9").substring(t("aboutUs.p9").indexOf(" ") + 1)}
            </p>
            <p className="italic">{t("aboutUs.p10")}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
    </section>
  );
};

export default AboutUs;
